import {hasSubclassFeature} from './subclasses/index.mjs';
import {martialArtsDie, getMonkFocusPoints as getMonkFocusMax} from './monk.mjs';

export const OPEN_HAND = 'monk:open-hand:technique';
export const ADDLE = 'monk:open-hand:addle';
export const hasOpenHand = actor => hasSubclassFeature(actor, OPEN_HAND);
export const canOfferOpenHand = (actor, attack, result) => hasOpenHand(actor)
  && attack?.kind === 'unarmed' && attack?.origin === 'monk' && attack?.monkAction === 'flurry'
  && ['hit', 'critical-hit'].includes(result?.outcome) && result.hpAfter > 0;
export const pendingOpenHand = combat => (combat?.eventLog || []).filter(e => e.type === 'openHandPending'
  && !(combat.eventLog || []).some(r => r.id === `${e.id}:resolved` && r.type === 'openHandResolved'));
export const openHandDecision = id => ({
  id: `${id}:open-hand`, featureId: OPEN_HAND, title: 'Open Hand Technique',
  prompt: 'Choose one technique for this Flurry of Blows hit. No additional Focus required.',
  choices: [
    {id:'addle', label:'Addle · No Opportunity Attacks', action:'apply'},
    {id:'push-5', label:'Push 5 ft · Strength save', action:'apply'},
    {id:'push-10', label:'Push 10 ft · Strength save', action:'apply'},
    {id:'push-15', label:'Push 15 ft · Strength save', action:'apply'},
    {id:'topple', label:'Topple · Dexterity save', action:'apply'},
    {id:'skip', label:'Skip'},
  ],
});
export const addleEffect = (id, attackerId, targetId, turnSequence) => ({
  id: `${id}:addle`, definitionId: ADDLE, name: 'Addled · Open Hand', icon:'✋',
  sourceFeature: OPEN_HAND, sourceCombatantId: attackerId, targetCombatantId: targetId,
  userVisible: true, removeOnSourceRemoval: false, stackPolicy:'refresh',
  stackKey: `open-hand:addle:${targetId}`, appliedAtTurnSequence: turnSequence,
  expiration: {type:'sourceTurn', combatantId:targetId, boundary:'start', remainingOccurrences:1},
  description: 'Cannot make Opportunity Attacks until the start of its next turn. Other Reactions remain available.',
});

// Wholeness of Body (Open Hand 6, 2024): a Bonus Action heal of the Martial Arts die + Wisdom modifier (minimum 1 HP),
// Wisdom modifier uses (minimum 1), all regained on a Long Rest. Saved as `classState.monk.wholenessOfBody = { spent, revision }`.
export const WHOLENESS_OF_BODY = 'monk:open-hand:wholeness-of-body';
export const wholenessOfBodyProfile = (character, wisdomModifier) => {
  if (!hasSubclassFeature(character, WHOLENESS_OF_BODY)) return { available: false, maximum: 0, spent: 0, remaining: 0, die: 0 };
  const modifier = Math.trunc(Number(wisdomModifier) || 0);
  const maximum = Math.max(1, modifier);
  const state = character?.classState?.monk?.wholenessOfBody || {};
  const spent = Math.min(maximum, Math.max(0, Math.trunc(Number(state.spent) || 0)));
  return { available: true, maximum, spent, remaining: maximum - spent, die: martialArtsDie(character), modifier, revision: Math.max(0, Math.trunc(Number(state.revision) || 0)) };
};
export const wholenessOfBodyHealing = (roll, wisdomModifier) => Math.max(1, Math.trunc(Number(roll) || 0) + Math.trunc(Number(wisdomModifier) || 0));
/** The state after a Long Rest, or null when nothing was spent (or for a Short Rest). */
export const wholenessOfBodyRestState = (character, type) => {
  const state = character?.classState?.monk?.wholenessOfBody;
  return type === 'long' && Number(state?.spent) > 0 ? { spent: 0, revision: (Number(state.revision) || 0) + 1 } : null;
};

// Fleet Step (Open Hand 11): after a Bonus Action other than Step of the Wind, Step of the Wind can follow it at once
// without a Bonus Action (once per turn here). The ledger's requests tell what the Bonus Action was.
export const FLEET_STEP = 'monk:open-hand:fleet-step';
export const canUseFleetStep = (character, ledger) => hasSubclassFeature(character, FLEET_STEP) && Boolean(ledger?.bonusActionSpent)
  && !Object.values(ledger?.requests || {}).some(request => /^monk:step-of-the-wind/.test(String(request?.kind || '')))
  && !(ledger?.turnStates || []).some(state => state.definitionId === FLEET_STEP);

// Quivering Palm (Open Hand 17): on an Unarmed Strike hit, spend 4 Focus Points to start vibrations in the target (one
// creature at a time, lasting Monk-level days). Ending them takes an action, or one attack of the Attack action: the
// target makes a Constitution save against the Focus save DC, taking 10d12 Force damage, or half on a success. Saved
// as `classState.monk.quiveringPalm = { targetId, targetName, resolutionId, startedAt }` (null once ended).
export const QUIVERING_PALM = 'monk:open-hand:quivering-palm';
export const QUIVERING_PALM_COST = 4;
export const activeQuiveringPalm = character => {
  const state = character?.classState?.monk?.quiveringPalm;
  return state && typeof state.targetId === 'string' && state.targetId ? state : null;
};
export const canOfferQuiveringPalm = (actor, attack, result) => hasSubclassFeature(actor, QUIVERING_PALM)
  && Number(actor?.monkFocus?.spent || 0) <= Math.max(0, getMonkFocusMax(actor) - QUIVERING_PALM_COST)
  && attack?.kind === 'unarmed' && ['hit', 'critical-hit'].includes(result?.outcome) && result.hpAfter > 0;
export const quiveringPalmDecision = resolutionId => ({
  id: `${resolutionId}:quivering-palm`, featureId: QUIVERING_PALM, title: 'Quivering Palm',
  prompt: 'Spend 4 Focus Points to start Quivering Palm vibrations in this creature? Later, end them with an action or one attack of your Attack action: a Constitution save, 10d12 Force damage (half on a success). Only one creature at a time.',
  choices: [{ id: 'use', label: 'Quivering Palm · 4 Focus', action: 'apply' }, { id: 'skip', label: 'Not now' }],
});
export const quiveringPalmRules = dc => ({
  delivery: 'saving-throw',
  save: { ability: 'constitution', dc, successDamageRule: 'half', failureDamageRule: 'full' },
  damage: { components: [{ formula: '10d12', type: 'force' }] },
});
